import { useEffect, useState } from "react";
import {
  Modal,
  MultiSelect,
  TextInput,
  NumberInput,
  Select,
  Switch,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Divider,
  Text,
} from "@mantine/core";
import type { RideType } from "../../types/domain";
import { ImageUploadField } from "../../components/common/ImageUploadField";

export interface RideTypeFormValues {
  name: { en: string; sw: string };
  description: { en: string; sw: string };
  vehicleType: "taxi" | "bike" | "bajaj";
  latraVehicleType: "car" | "motorcycle" | "tricycle";
  minimumFare: number;
  fareUnderOneKm: number;
  farePerKm: number;
  farePerMin: number;
  waitingFeePerMin: number;
  cancellationFee: number;
  /** Blank = use the global default commission from Settings. */
  commissionPercentage: number | null;
  /** Other ride types this one's drivers may also take trips for. */
  alsoServes: string[];
  numberOfPassengers: number;
  imageUrl: string;
  position: number;
  isActive: boolean;
}

const EMPTY: RideTypeFormValues = {
  name: { en: "", sw: "" },
  description: { en: "", sw: "" },
  vehicleType: "taxi",
  latraVehicleType: "car",
  minimumFare: 2000,
  fareUnderOneKm: 2000,
  farePerKm: 800,
  farePerMin: 50,
  waitingFeePerMin: 100,
  cancellationFee: 0,
  commissionPercentage: null,
  alsoServes: [],
  numberOfPassengers: 4,
  imageUrl: "",
  position: 1,
  isActive: true,
};

function fromRideType(rt: RideType): RideTypeFormValues {
  return {
    name: { en: rt.name?.en ?? "", sw: rt.name?.sw ?? "" },
    description: { en: rt.description?.en ?? "", sw: rt.description?.sw ?? "" },
    vehicleType: rt.vehicleType ?? "taxi",
    latraVehicleType: rt.latraVehicleType ?? "car",
    minimumFare: rt.minimumFare ?? 0,
    fareUnderOneKm: rt.fareUnderOneKm ?? 0,
    farePerKm: rt.farePerKm ?? 0,
    farePerMin: rt.farePerMin ?? 0,
    waitingFeePerMin: rt.waitingFeePerMin ?? 0,
    cancellationFee: rt.cancellationFee ?? 0,
    commissionPercentage: rt.commissionPercentage ?? null,
    alsoServes: (rt.alsoServes ?? []).map(String),
    numberOfPassengers: rt.numberOfPassengers ?? 1,
    imageUrl: rt.imageUrl ?? "",
    position: rt.position ?? 1,
    isActive: rt.isActive ?? true,
  };
}

export function RideTypeFormModal({
  opened,
  onClose,
  editing,
  loading,
  onSubmit,
  rideTypes = [],
}: {
  opened: boolean;
  onClose: () => void;
  editing: RideType | null;
  /** Every ride type, for the "Can also serve" picker. */
  rideTypes?: RideType[];
  loading: boolean;
  onSubmit: (values: RideTypeFormValues) => void;
}) {
  const [v, setV] = useState<RideTypeFormValues>(EMPTY);

  useEffect(() => {
    if (opened) setV(editing ? fromRideType(editing) : EMPTY);
  }, [opened, editing]);

  const valid = v.name.en.trim() && v.name.sw.trim() && v.imageUrl.trim();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editing ? "Edit ride type" : "New ride type"}
      size="lg"
      centered
    >
      <Stack>
        <Text size="xs" c="dimmed" fw={600} tt="uppercase">
          Name & description (bilingual)
        </Text>
        <SimpleGrid cols={2}>
          <TextInput
            label="Name (English)"
            value={v.name.en}
            onChange={(e) =>
              setV({ ...v, name: { ...v.name, en: e.currentTarget.value } })
            }
            required
          />
          <TextInput
            label="Name (Swahili)"
            value={v.name.sw}
            onChange={(e) =>
              setV({ ...v, name: { ...v.name, sw: e.currentTarget.value } })
            }
            required
          />
          <TextInput
            label="Description (English)"
            value={v.description.en}
            onChange={(e) =>
              setV({
                ...v,
                description: { ...v.description, en: e.currentTarget.value },
              })
            }
          />
          <TextInput
            label="Description (Swahili)"
            value={v.description.sw}
            onChange={(e) =>
              setV({
                ...v,
                description: { ...v.description, sw: e.currentTarget.value },
              })
            }
          />
        </SimpleGrid>

        <Divider label="Vehicle" labelPosition="left" />
        <SimpleGrid cols={2}>
          <Select
            label="Vehicle type"
            data={["taxi", "bike", "bajaj"]}
            value={v.vehicleType}
            onChange={(val) =>
              setV({ ...v, vehicleType: (val as RideTypeFormValues["vehicleType"]) ?? "taxi" })
            }
            allowDeselect={false}
          />
          <Select
            label="LATRA type"
            data={["car", "motorcycle", "tricycle"]}
            value={v.latraVehicleType}
            onChange={(val) =>
              setV({
                ...v,
                latraVehicleType:
                  (val as RideTypeFormValues["latraVehicleType"]) ?? "car",
              })
            }
            allowDeselect={false}
          />
        </SimpleGrid>

        <Divider label="Fares (TZS)" labelPosition="left" />
        <SimpleGrid cols={{ base: 2, sm: 3 }}>
          <NumberInput
            label="Starting Fare"
            value={v.minimumFare}
            onChange={(val) => setV({ ...v, minimumFare: Number(val) || 0 })}
            min={0}
          />
          <NumberInput
            label="Min Fare"
            description="Flat fare for sub-1km rides"
            value={v.fareUnderOneKm}
            onChange={(val) => setV({ ...v, fareUnderOneKm: Number(val) || 0 })}
            min={0}
          />
          <NumberInput
            label="Per km"
            value={v.farePerKm}
            onChange={(val) => setV({ ...v, farePerKm: Number(val) || 0 })}
            min={0}
          />
          <NumberInput
            label="Per min"
            value={v.farePerMin}
            onChange={(val) => setV({ ...v, farePerMin: Number(val) || 0 })}
            min={0}
          />
          <NumberInput
            label="Waiting / min"
            value={v.waitingFeePerMin}
            onChange={(val) => setV({ ...v, waitingFeePerMin: Number(val) || 0 })}
            min={0}
          />
          <NumberInput
            label="Cancellation fee"
            description="After driver arrives. Multiple of 500; 0 = none."
            value={v.cancellationFee}
            onChange={(val) => setV({ ...v, cancellationFee: Number(val) || 0 })}
            min={0}
            step={500}
            error={v.cancellationFee % 500 !== 0 ? "Must be a multiple of 500" : undefined}
          />
          <NumberInput
            label="Passengers"
            value={v.numberOfPassengers}
            onChange={(val) =>
              setV({ ...v, numberOfPassengers: Number(val) || 1 })
            }
            min={1}
          />
          <NumberInput
            label="Display position"
            value={v.position}
            onChange={(val) => setV({ ...v, position: Number(val) || 1 })}
            min={1}
          />
        </SimpleGrid>

        <Divider label="Commission" labelPosition="left" />
        <NumberInput
          label="Commission % (optional)"
          description="Leave blank to use the global default from Settings. 0 means a genuinely zero-commission category."
          value={v.commissionPercentage ?? ""}
          onChange={(val) =>
            setV({
              ...v,
              commissionPercentage: val === "" || val === null ? null : Number(val),
            })
          }
          min={0}
          max={100}
          decimalScale={2}
          placeholder="Using global default"
        />

        <Divider label="Dispatch" labelPosition="left" />
        <MultiSelect
          label="Can also serve"
          description="This ride type's drivers also get trips booked as these ride types, after that ride type's own drivers in the same search radius. The rider pays that ride type's fare. E.g. XL → Safiri Haraka."
          placeholder={v.alsoServes.length ? undefined : "Only its own trips"}
          data={rideTypes
            .filter((rt) => rt._id !== editing?._id)
            .map((rt) => ({ value: rt._id, label: rt.name?.en ?? rt._id }))}
          value={v.alsoServes}
          onChange={(alsoServes) => setV({ ...v, alsoServes })}
          clearable
          searchable
        />

        <ImageUploadField
          label="Icon / image"
          value={v.imageUrl}
          onChange={(url) => setV({ ...v, imageUrl: url ?? "" })}
          folder="ride-types"
          required
        />

        <Switch
          label="Active"
          checked={v.isActive}
          onChange={(e) => setV({ ...v, isActive: e.currentTarget.checked })}
        />

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={!valid}
            onClick={() => onSubmit(v)}
          >
            {editing ? "Save changes" : "Create"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
